import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useT } from '@/store';
import { useTenantId } from '@/hooks/useTenant';
import { qk } from '@/lib/queryKeys';
import { Panel, EmptyState, ErrorState, SkeletonRows } from '@/components/ui/Panel';

/**
 * Dispečerio krūvio balansas — whose day is already fuller than their shift,
 * and who has nothing left to do, in one glance.
 *
 * WHY A PANEL AND NOT A COLUMN SOMEWHERE. Imbalance is a comparison. The
 * console already knew every number this shows — Smart Match computes a
 * driver's remaining shift to decide whether he can take one more load, and
 * the orders table knows who holds what — but both answer per row, and a
 * per-row answer you have to read nine times is one nobody reads. The
 * question a dispatcher asks at 11:00 is about the fleet, so the answer has
 * to be about the fleet.
 *
 * WORST FIRST, always. The server orders the rows over_shift → idle →
 * light → balanced → off, so the two rows that need doing something about
 * are at the top rather than wherever the alphabet put them. This component
 * does not re-sort: a panel that reorders what the engine ranked is a panel
 * that can disagree with it.
 *
 * ONE THRESHOLD IS A RULE, THE OTHER IS A BAND, and the wording keeps them
 * apart. `over_shift` means this driver's remaining work does not fit in
 * what is left of his shift — computed from the same derivation Smart Match
 * excludes a candidate on, so the two screens can never contradict each
 * other. `light` is a display band with no authority: nothing in the
 * product acts on it, and the fleet's own average is shown beside it
 * precisely so a quiet Tuesday does not read as nine underloaded drivers.
 *
 * The estimate marker is not decoration. A load that has not been delivered
 * has no measured duration, so its cost to the day is the same generous
 * figure Smart Match checks a shift against — and a row whose total leans on
 * that says so, the same rule the client portal's eta_source follows.
 */
const TONE = {
  over_shift: 'text-[#FCA5A5]',
  idle: 'text-amber',
  light: 'text-dim',
  balanced: 'text-green',
  off: 'text-dim',
};

const BAR = {
  over_shift: 'bg-[#FCA5A5]',
  idle: 'bg-white/15',
  light: 'bg-amber/60',
  balanced: 'bg-gradient-to-r from-accent-solid to-accent-hi',
  off: 'bg-white/10',
};

export function LoadBalancePanel({ date }) {
  const t = useT();
  const tenantId = useTenantId();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: qk.loadBalance(tenantId, date ?? 'today'),
    queryFn: () => api(`/api/tenants/${tenantId}/load-balance${date ? `?date=${date}` : ''}`),
    enabled: Boolean(tenantId),
    // Shifts burn down and loads get delivered; a balance older than this is
    // describing a morning that has moved on. Not polled: a dispatcher opens
    // this panel to make a decision, and the decision takes less than a
    // minute.
    staleTime: 60_000,
  });

  if (isError) return <Panel title={t('loadBalance.title')}><ErrorState error={error} /></Panel>;

  const rows = data?.drivers ?? [];
  const summary = data?.summary;

  return (
    <Panel
      title={t('loadBalance.title')}
      actions={
        summary ? (
          <span className="text-[11px] text-dim">
            {t('loadBalance.fleetAverage', { pct: summary.average_load_pct ?? 0 })}
            {summary.over_shift > 0
              ? ` · ${t('loadBalance.overCount', { n: summary.over_shift })}`
              : ''}
            {summary.idle > 0 ? ` · ${t('loadBalance.idleCount', { n: summary.idle })}` : ''}
          </span>
        ) : null
      }
    >
      {isLoading ? (
        <SkeletonRows rows={4} />
      ) : rows.length === 0 ? (
        <EmptyState>{t('loadBalance.noDrivers')}</EmptyState>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((row) => (
            <DriverLoadRow key={row.driver_id} row={row} />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function DriverLoadRow({ row }) {
  const t = useT();
  // Clamped for the BAR only. The number beside it is never clamped — a
  // driver at 140% of his shift is the whole reason this panel exists, and
  // rounding that down to a full bar would hide it.
  const width = Math.min(row.load_pct ?? 0, 100);

  return (
    <li className="rounded-[10px] border border-line bg-white/2 p-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[12.5px] font-semibold">{row.full_name}</span>
        <span className={`text-[11px] font-semibold ${TONE[row.verdict] ?? 'text-dim'}`}>
          {t(`loadBalance.verdict.${row.verdict}`)}
        </span>
      </div>

      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/6">
        <div
          className={`h-full rounded-full transition-[width] duration-500 ${BAR[row.verdict] ?? 'bg-white/15'}`}
          style={{ width: `${width}%` }}
        />
      </div>

      <div className="mt-1 flex flex-wrap justify-between gap-x-3 text-[10.5px] text-dim">
        <span>
          {t('loadBalance.orders', { n: row.order_count, done: row.orders_done })}
          {row.planned_tons > 0 ? ` · ${t('loadBalance.tons', { n: row.planned_tons })}` : ''}
        </span>
        <span>
          {/* The tilde is the estimate marker, the same one Smart Match uses
              on an availability derived from an order's stage rather than
              observed. */}
          {t('loadBalance.workLeft', {
            n: `${row.committed_minutes_is_estimate ? '≈' : ''}${row.remaining_work_minutes}`,
          })}
          {' · '}
          {t('loadBalance.shiftLeft', { n: Math.round(row.shift_remaining_minutes) })}
        </span>
      </div>
    </li>
  );
}
